import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import EmbedModal from './EmbedModal';

const { FiPlay, FiPause, FiVolume2, FiShare2, FiRadio } = FiIcons;

const RadioPlayer = ({ streamUrl, logoUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [metadata, setMetadata] = useState({ title: "Connecting...", artist: "On Air" });
  const [isLoading, setIsLoading] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const audioRef = useRef(null);

  // CHANGE THIS: If you want to force a specific domain in the embed code
  // otherwise leave it as window.location.href for dynamic detection
  const productionUrl = window.location.href; 

  const fetchMetadata = () => {
    const callbackName = 'radiojar_cb_' + Math.floor(Math.random() * 1000000);
    window[callbackName] = (data) => {
      if (data && (data.title || data.artist)) {
        setMetadata({ 
          title: data.title || "Live Broadcast", 
          artist: data.artist || "Radio Station" 
        });
      }
      delete window[callbackName];
      const script = document.getElementById(callbackName);
      if (script) document.body.removeChild(script);
    };

    const script = document.createElement('script');
    script.id = callbackName;
    script.src = `https://www.radiojar.com/api/stations/98mxa4t2c0nwv/now_playing/?callback=${callbackName}&_=${Date.now()}`;
    script.async = true;
    document.body.appendChild(script);
  };

  useEffect(() => {
    fetchMetadata();
    const interval = setInterval(fetchMetadata, 15000);
    return () => clearInterval(interval);
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
          fetchMetadata();
        })
        .catch(() => setIsLoading(false));
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto px-4">
      <div className="bg-white rounded-[3rem] shadow-2xl border border-gray-100 overflow-hidden flex flex-col md:flex-row min-h-[450px]">
        
        {/* Left: Branding & Visual */}
        <div className="w-full md:w-2/5 bg-gray-900 relative overflow-hidden group">
          <img 
            src={logoUrl} 
            alt="Station" 
            className={`w-full h-full object-cover transition-transform duration-[5000ms] ${isPlaying ? 'scale-110' : 'scale-100'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent opacity-60"></div>
          
          <div className="absolute bottom-8 left-8 right-8">
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full w-fit mb-4">
              <SafeIcon icon={FiRadio} className="text-[#00AEEF]" />
              <span className="text-[10px] font-black text-white uppercase tracking-[0.2em]">Live Stream</span>
            </div>
            <h3 className="text-white text-2xl font-black uppercase tracking-tighter">Console Radio</h3>
          </div>
        </div>

        {/* Right: metadata & Controls */}
        <div className="flex-1 p-8 md:p-16 flex flex-col justify-center relative">
          
          <button 
            onClick={() => setIsShareOpen(true)}
            className="absolute top-8 right-8 p-3 bg-gray-50 hover:bg-[#00AEEF] hover:text-white text-gray-400 rounded-2xl transition-all duration-300 group"
            title="Share Player"
          >
            <SafeIcon icon={FiShare2} className="text-xl" />
          </button>

          {/* LARGE METADATA */}
          <div className="mb-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={metadata.title}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <h2 className="text-4xl md:text-6xl font-black text-gray-900 leading-[1.1] tracking-tighter">
                  {metadata.title}
                </h2>
                <div className="flex items-center gap-3">
                  <div className="h-1 w-12 bg-[#00AEEF] rounded-full"></div>
                  <p className="text-xl md:text-2xl font-bold text-[#00AEEF] uppercase tracking-wider">
                    {metadata.artist}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Player controls */}
          <div className="flex flex-col sm:flex-row items-center gap-10">
            <button
              onClick={togglePlay}
              className="w-24 h-24 rounded-[2rem] bg-[#00AEEF] text-white flex items-center justify-center shadow-xl shadow-blue-100 hover:scale-105 transition-transform active:scale-95 shrink-0"
            >
              {isLoading ? (
                <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : isPlaying ? (
                <SafeIcon icon={FiPause} className="text-4xl" />
              ) : (
                <SafeIcon icon={FiPlay} className="text-4xl ml-1" />
              )}
            </button>

            <div className="flex-1 w-full space-y-4">
              <div className="flex justify-between text-[11px] font-black text-gray-400 uppercase tracking-[0.2em]">
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiVolume2} className="text-sm" />
                  Volume
                </div>
                <span className="text-[#00AEEF]">{Math.round(volume * 100)}%</span>
              </div>
              <input 
                type="range" 
                min="0" max="1" step="0.01" 
                value={volume}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setVolume(v);
                  audioRef.current.volume = v;
                }}
                className="w-full h-2 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#00AEEF]"
              />
            </div>
          </div>
        </div>
      </div>

      <EmbedModal 
        isOpen={isShareOpen} 
        onClose={() => setIsShareOpen(false)} 
        shareUrl={productionUrl}
      />

      <audio ref={audioRef} src={streamUrl} crossOrigin="anonymous" />
    </div>
  );
};

export default RadioPlayer;